import React from "react";
import { Icon } from "antd";

/**
 * Base LocalStorage key for priority-banner dismissals. The real key is
 * per-company (see `getPriorityDismissKey`) because localStorage is shared across
 * every company signed in on the same browser: without the company suffix,
 * dismissing under one company would hide the banner for the others too, and a
 * later dismissal by one company would overwrite another's.
 */
export const PRIORITY_BANNER_KEY = "status_priority_dismissed_key";

/**
 * The company the banner dismissal is scoped to. Falls back to `"anon"` before
 * login / when the id is missing so a pre-login dismissal never leaks into a
 * real company session.
 */
export const getDismissScopeId = () => {
	try {
		const id = localStorage.getItem("companyID");
		if (!id || id === "null" || id === "undefined") return "anon";
		return id;
	} catch (e) {
		return "anon";
	}
};

// Per-company storage key: each company login tracks its own dismissal.
export const getPriorityDismissKey = () => `${PRIORITY_BANNER_KEY}_${getDismissScopeId()}`;

/**
 * Value stored under the dismiss key: `<docId>_<updatedAt>`. Single source of
 * truth for both the write (on dismiss) and the read (visibility check) so the
 * two can never drift. The embedded `updatedAt` means any admin edit in
 * Firestore produces a new signature and re-shows the banner for everyone.
 */
export const getPriorityBannerSignature = (bannerData) => {
	const id = (bannerData && bannerData.id) || "";
	const updatedAt = (bannerData && bannerData.updatedAt) || "";
	return `${id}_${updatedAt}`;
};

// Visual theme per notice category.
const THEMES = {
	incident: {
		background: "#fff1f0",
		border: "#ffa39e",
		color: "#a8071a",
		iconColor: "#cf1322",
		icon: "exclamation-circle",
		badge: "Critical Issue",
	},
	update: {
		background: "#f6ffed",
		border: "#b7eb8f",
		color: "#135200",
		iconColor: "#389e0d",
		icon: "notification",
		badge: "System Update",
	},
};

// Map the free-text Firestore `type` onto one of the known themes.
const resolveTheme = (type) => {
	const value = (type || "").toLowerCase();
	if (
		value.includes("incident") ||
		value.includes("critical") ||
		value.includes("outage") ||
		value.includes("bug") ||
		value.includes("error")
	) {
		return THEMES.incident;
	}
	return THEMES.update;
};

const PriorityBanner = ({ bannerData, onDismiss }) => {
	if (!bannerData) return null;

	const { title, type, link } = bannerData;
	const theme = resolveTheme(type);

	const wrapperStyle = {
		width: "100%",
		background: theme.background,
		borderBottom: `1px solid ${theme.border}`,
		color: theme.color,
		fontSize: 13,
		lineHeight: 1.4,
		padding: "8px 44px 8px 16px",
		position: "relative",
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 8,
	};

	const badgeStyle = {
		background: theme.color,
		color: theme.background,
		fontSize: 11,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: 0.4,
		padding: "2px 8px",
		borderRadius: 3,
		whiteSpace: "nowrap",
	};

	const linkStyle = {
		color: theme.color,
		fontWeight: 600,
		textDecoration: "underline",
		cursor: "pointer",
		whiteSpace: "nowrap",
	};

	const closeStyle = {
		position: "absolute",
		right: 14,
		top: "50%",
		transform: "translateY(-50%)",
		cursor: "pointer",
		fontSize: 14,
		color: theme.color,
	};

	const handleClose = () => {
		try {
			localStorage.setItem(getPriorityDismissKey(), getPriorityBannerSignature(bannerData));
		} catch (e) {
			/* ignore storage errors */
		}
		if (onDismiss) onDismiss();
	};

	return (
		<div style={wrapperStyle} role="alert">
			<Icon type={theme.icon} theme="filled" style={{ fontSize: 16, color: theme.iconColor }} />
			<span style={badgeStyle}>{theme.badge}</span>
			<span style={{ fontWeight: 600 }}>{title}</span>
			{link ? (
				<a style={linkStyle} href={link} target="_blank" rel="noopener noreferrer">
					Learn More
				</a>
			) : null}
			<Icon type="close" style={closeStyle} onClick={handleClose} title="Dismiss" />
		</div>
	);
};

export default PriorityBanner;
